import { useEffect, useState } from "react"

import { liveChatPlans, useChat, type ChatPlan } from "@/features/chat"
import { useCreditsPurchase } from "@/features/advisors"
import { cn } from "@/lib/utils"
import { showErrorToast } from "@/stores/toast-store"

import HeroImage from "@/assets/img/profile/stars-purchase/hero.webp"
import SuccessImage from "@/assets/img/profile/stars-purchase/success.webp"
import StarIcon from "@/assets/img/profile/stars-purchase/star.webp"
import RadioChecked from "@/assets/img/profile/stars-purchase/radio-checked.svg?react"
import CloseIcon from "@/assets/img/profile/stars-purchase/x-close.svg?react"
import ArrowMicro from "@/assets/svg/subscription/arrow_micro_new.svg?react"
import LeafDecor from "@/assets/img/live-chat/leaf_decor.svg?react"

interface StarsPurchaseModalProps {
  onClose: () => void
}

/** Profile stars top-up (Figma: Stars Purchase). Renders above the profile overlay. */
export function StarsPurchaseModal({ onClose }: StarsPurchaseModalProps) {
  const { userMainSubscriptionId } = useChat()
  const { purchase, isLoading, error } = useCreditsPurchase({ userMainSubscriptionId })
  const [selectedPlan, setSelectedPlan] = useState<ChatPlan>(liveChatPlans[1])
  const [succeeded, setSucceeded] = useState(false)

  useEffect(() => {
    if (!error) return
    showErrorToast("Payment error", "Please retry or check your balance")
  }, [error])

  const handlePurchase = () => {
    if (!selectedPlan || isLoading) return
    void purchase(selectedPlan, () => setSucceeded(true))
  }

  return (
    <div
      data-slot="stars-purchase-modal"
      className="fixed inset-0 z-[1100] mx-auto flex max-h-[100dvh] w-full max-w-app flex-col overflow-y-auto bg-dark-base [scrollbar-width:none]"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 z-20 [&_svg]:size-6"
      >
        <CloseIcon />
      </button>

      <img
        src={HeroImage}
        alt=""
        width={414}
        height={240}
        className="h-[240px] w-full shrink-0 object-cover object-top"
      />

      <div className="relative z-10 -mt-24 flex flex-1 flex-col px-4 pb-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">
            Make the most out of session
          </h1>
          <p className="font-body text-base text-purple-light/70">
            Enjoy the freedom to ask more, dive deeper, and get the answers you truly need -
            without interruptions!
          </p>
        </div>

        <div className="mt-[52px] grid grid-cols-2 gap-3">
          {liveChatPlans.map((plan) => {
            const isSelected = selectedPlan?.code === plan.code
            return (
              <button
                type="button"
                key={plan.code}
                onClick={() => {
                  if (!isLoading) setSelectedPlan(plan)
                }}
                className={cn(
                  "relative flex flex-col items-center gap-1 rounded-2xl py-4",
                  isSelected
                    ? "bg-main-purple/10 outline outline-2 outline-main-purple"
                    : "bg-purple-light/10",
                )}
              >
                {isSelected && (
                  <span className="absolute -right-2 -top-2 [&_svg]:size-6">
                    <RadioChecked />
                  </span>
                )}
                <p className="font-display text-xl font-medium text-purple-light">
                  {plan.creditsCount} stars
                </p>
                <PlanPrice plan={plan} />
                <PlanBadges plan={plan} />
              </button>
            )
          })}
        </div>

        <div className="mt-[52px] flex flex-1 flex-col items-center justify-end gap-4">
          <div className="flex items-center gap-1">
            <img src={StarIcon} alt="" width={20} height={20} className="size-5 object-contain" />
            <p className="font-body text-sm text-purple-light/70">
              60 stars = 1 min of consultation
            </p>
          </div>

          <PurchaseButton isLoading={isLoading} onClick={handlePurchase} />

          <p className="font-body text-sm text-purple-light/70">
            One-time payment is non-refundable
          </p>
        </div>
      </div>

      {succeeded && <SuccessSheet onDone={onClose} />}
    </div>
  )
}

function PlanPrice({ plan }: { plan: ChatPlan }) {
  if (plan.discount === 0) {
    return (
      <p className="font-body text-xs text-purple-light/70">${plan.unitAmount}</p>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <span className="relative font-body text-xs text-purple-light/40">
        ${plan.unitAmountNoDiscount}
        <span className="absolute inset-x-0 top-1/2 h-px bg-peach" />
      </span>
      <span className="[&_svg]:size-2.5">
        <ArrowMicro />
      </span>
      <span className="font-body text-xs text-purple-light/70">${plan.unitAmount}</span>
    </div>
  )
}

function PlanBadges({ plan }: { plan: ChatPlan }) {
  if (plan.discount === 0) {
    return (
      <span className="flex h-[26px] items-center rounded-[15px] border border-purple-light/20 px-2 font-body text-[10px] font-semibold text-purple-light/40">
        NO DISCOUNT
      </span>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-1">
      <span className="flex h-[26px] items-center rounded-[15px] bg-green/20 px-2 font-body text-[10px] font-semibold text-green">
        {plan.discount}% OFF
      </span>
      {plan.isPopular && (
        <span className="flex h-[26px] items-center gap-1 rounded-[13px] bg-gold/20 px-1.5 font-body text-[10px] font-medium text-gold [&_svg]:h-3.5 [&_svg]:w-1.5 [&_svg:last-child]:-scale-x-100">
          <LeafDecor />
          POPULAR
          <LeafDecor />
        </span>
      )}
    </div>
  )
}

function PurchaseButton({
  isLoading,
  onClick,
}: {
  isLoading: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className={cn(
        "flex h-14 w-full items-center justify-center gap-2 rounded-[80px] font-body text-base font-semibold transition-opacity",
        isLoading
          ? "bg-purple-light/10 text-purple-light/70"
          : "bg-gradient-to-b from-peach to-purple-accent text-white hover:opacity-85",
      )}
    >
      {isLoading ? (
        <>
          <span className="size-6 animate-spin rounded-full border-2 border-purple-light/70 border-t-transparent" />
          Purchasing...
        </>
      ) : (
        "Continue"
      )}
    </button>
  )
}

function SuccessSheet({ onDone }: { onDone: () => void }) {
  return (
    <div className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]">
      <div className="flex w-full flex-col items-center gap-4 rounded-t-3xl bg-dark-surface px-4 pb-safe pt-2">
        <img
          src={SuccessImage}
          alt=""
          width={304}
          height={272}
          className="h-[272px] w-[304px] object-cover"
        />
        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="font-display text-2xl font-medium text-purple-light">
            Thank you for your purchase
          </h2>
          <p className="font-body text-base text-purple-light/70">
            Your stars are being added to your balance right now - it might take a few minutes.
          </p>
        </div>
        <button
          type="button"
          onClick={onDone}
          className="flex h-14 w-full items-center justify-center rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-85"
        >
          Got it
        </button>
      </div>
    </div>
  )
}
